/*
 * Shared GA4 owner for the public homepage, SEO landing pages, and the local
 * study app. Keep this file independent from the flat app module scope: it is
 * also loaded by standalone marketing pages.
 */
(function initializeMemoraAnalytics(global) {
    'use strict';

    if (!global || global.MemoraAnalytics?.version === 1) return;

    const MEASUREMENT_ID = 'G-HEGTC5J53S';
    const PREFERENCE_KEY = 'memora_analytics_enabled_v1';
    const browserOptOut = global.navigator?.globalPrivacyControl === true;
    let enabled = false;
    let persisted = true;
    try { enabled = !browserOptOut && global.localStorage.getItem(PREFERENCE_KEY) !== '0'; }
    catch (_) { persisted = false; }
    let started = false;
    let tagReady = false;
    let configured = false;
    const pendingEvents = [];
    const activeJobs = new Map();
    const featureByRoute = Object.freeze({
        'learn-quizzes': 'quizzes', 'quizzes': 'quizzes', 'learn-guide': 'study_guide',
        'learn-guide-finishing': 'study_guide_finishing', 'learn-flashcards': 'flashcards',
        'learn-occlusion': 'closed_occlusion', 'learn-guided': 'guided_tutor', 'learn-clinic': 'clinic',
        'dashboard': 'dashboard', 'sessions': 'sessions'
    });
    const featureEnums = new Set([...Object.values(featureByRoute), 'ask', 'voice', 'settings', 'privacy', 'saved_content', 'stats', 'sources', 'calendar']);
    const FIRST_QUIZ_KEY = 'memora_analytics_first_quiz_generated_v1';
    // Guide pages are folders (/quiz-tools/); a slug is the folder name.
    const landingSlugs = new Set([
        'quiz-tools',
        'high-yield-quizzes',
        'study-library',
        'study-planner'
    ]);
    const sourceEnums = new Set(['import', 'quizgen', 'pdf-tutor', 'desktop-drop', 'study-guide']);
    const exportFormats = new Set(['anki', 'quizlet', 'csv', 'json']);
    const eventParams = Object.freeze({
        seo_cta_click: new Set(['page_slug', 'cta_position', 'destination_slug']),
        app_open_from_seo: new Set(['landing_page', 'cta_position']),
        import_started: new Set(['source', 'source_count']),
        first_quiz_generated: new Set(['question_count']),
        study_set_exported: new Set(['format', 'question_count']),
        feature_used: new Set(['feature', 'action']),
        feature_selected: new Set(['feature']),
        generation_started: new Set(['feature']),
        generation_result: new Set(['feature', 'outcome', 'duration_ms', 'queue_wait_ms', 'usable_count']),
        study_flow_step: new Set(['step'])
    });
    let firstQuizTracked = false;

    const dataLayer = global.dataLayer = global.dataLayer || [];
    const gtag = typeof global.gtag === 'function'
        ? global.gtag
        : function memoraGtag() { dataLayer.push(arguments); };
    global.gtag = gtag;
    global['ga-disable-' + MEASUREMENT_ID] = !enabled;

    const documentRef = global.document;
    const locationRef = global.location;

    function loadGoogleTag() {
        if (!documentRef?.head || documentRef.querySelector(`script[data-memora-ga="${MEASUREMENT_ID}"]`)) return;
        const script = documentRef.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
        script.dataset.memoraGa = MEASUREMENT_ID;
        script.onload = () => { tagReady = true; configureGoogleTag(); };
        documentRef.head.appendChild(script);
    }

    function configureGoogleTag() {
        const params = new URLSearchParams(locationRef?.search || '');
        if (!enabled || !tagReady || configured) return;
        const debugEnabled = ['localhost', '127.0.0.1', '[::1]'].includes(locationRef?.hostname)
            || params.get('ga_debug') === '1'
            || global.__MEMORA_DEVELOPER_MARKER__ === true;
        const safeUrl = (url) => { if (!url) return ''; try { const parsed = new URL(url, locationRef.href); return parsed.origin + parsed.pathname; } catch (_) { return ''; } };
        gtag('config', MEASUREMENT_ID, {
            page_location: safeUrl(locationRef.href), page_referrer: safeUrl(documentRef?.referrer || ''),
            page_title: 'Memoraful', allow_google_signals: false, allow_ad_personalization_signals: false,
            ...(debugEnabled ? { debug_mode: true } : {})
        });
        configured = true;
        while (enabled && pendingEvents.length) { const [name, params] = pendingEvents.shift(); gtag('event', name, params); }
    }

    function start() {
        if (!enabled) return;
        if (!started) { started = true; gtag('js', new Date()); loadGoogleTag(); }
        if (typeof global.requestIdleCallback === 'function') global.requestIdleCallback(configureGoogleTag, { timeout: 3000 });
        else global.setTimeout(configureGoogleTag, 400);
    }

    function getState() { return { enabled, browserOptOut, persisted }; }

    function syncPublicControls() {
        documentRef?.querySelectorAll?.('[data-memora-analytics-toggle]').forEach((control) => {
            control.checked = enabled; control.disabled = browserOptOut;
        });
        documentRef?.querySelectorAll?.('[data-memora-analytics-state]').forEach((control) => {
            control.textContent = !persisted ? 'Off for this visit. Browser storage is unavailable.'
                : browserOptOut ? 'Off because of your browser’s privacy settings.'
                    : enabled ? 'On in this browser. You can turn it off at any time.' : 'Off in this browser. New analytics events are blocked.';
        });
    }

    function applyPreference(next) {
        enabled = next === true && !browserOptOut;
        global['ga-disable-' + MEASUREMENT_ID] = !enabled;
        if (!enabled) { activeJobs.clear(); pendingEvents.length = 0; configured = false; }
        if (!enabled && documentRef) {
            const domains = (locationRef?.hostname || '').split('.');
            const suffixes = [''];
            for (let i = 0; i < domains.length - 1; i++) suffixes.push('; domain=' + domains.slice(i).join('.'), '; domain=.' + domains.slice(i).join('.'));
            for (const name of ['_ga', '_ga_' + MEASUREMENT_ID.slice(2).replace(/-/g, '_')]) {
                for (const suffix of suffixes) { try { documentRef.cookie = name + '=; Max-Age=0; path=/' + suffix; } catch (_) { } }
            }
        }
        if (enabled) start();
        syncPublicControls();
        if (typeof global.CustomEvent === 'function') global.dispatchEvent(new global.CustomEvent('memora:analytics-preference-changed', { detail: getState() }));
    }

    function setEnabled(next) {
        try { global.localStorage.setItem(PREFERENCE_KEY, next === true ? '1' : '0'); persisted = true; }
        catch (_) { persisted = false; next = false; }
        applyPreference(next);
        return getState();
    }

    start();

    function toSlug(value) {
        return String(value || '').trim().replace(/^\/+/, '').replace(/(?:^|\/)index\.html$/, '').replace(/\/+$/, '');
    }

    function normalizeSlug(value) {
        const raw = toSlug(value);
        return landingSlugs.has(raw) ? raw : '';
    }

    function currentPageSlug() {
        const path = toSlug(locationRef?.pathname);
        return path && path !== 'home' ? path : 'home';
    }

    function normalizeSource(value) {
        const raw = String(value || '').trim().toLowerCase();
        return sourceEnums.has(raw) ? raw : 'import';
    }

    function cleanEventParams(eventName, params = {}) {
        const allowed = eventParams[eventName];
        if (!allowed) return {};
        const clean = {};
        Object.entries(params).forEach(([key, value]) => {
            if (!allowed.has(key)) return;
            if (typeof value === 'number') {
                if (Number.isFinite(value)) clean[key] = Math.max(0, Math.min(key.endsWith('_ms') ? 86400000 : 10000, Math.round(value)));
                return;
            }
            const text = String(value || '').trim();
            const enums = { action: new Set(['open', 'request', 'voice_turn']), feature: featureEnums, outcome: new Set(['ready', 'partial', 'failed', 'cancelled']), step: new Set(['upload', 'generate', 'study']), source: sourceEnums, format: exportFormats, page_slug: landingSlugs, landing_page: landingSlugs, destination_slug: new Set([...landingSlugs, 'app', 'home']), cta_position: new Set(['primary', 'secondary', 'hero', 'footer']) };
            if (enums[key]?.has(text)) clean[key] = text;
        });
        return clean;
    }

    function track(eventName, params = {}) {
        if (!enabled || !eventParams[eventName]) return false;
        const clean = cleanEventParams(eventName, params);
        if ((eventParams[eventName].has('feature') && !clean.feature) || (eventName === 'study_flow_step' && !clean.step)) return false;
        if (!configured) {
            if (pendingEvents.length >= 100) pendingEvents.shift();
            pendingEvents.push([eventName, clean]);
        } else gtag('event', eventName, clean);
        return true;
    }

    function wasFirstQuizTracked() {
        if (firstQuizTracked) return true;
        try {
            firstQuizTracked = global.localStorage?.getItem(FIRST_QUIZ_KEY) === '1';
        } catch (_) { }
        return firstQuizTracked;
    }

    function markFirstQuizTracked() {
        firstQuizTracked = true;
        try { global.localStorage?.setItem(FIRST_QUIZ_KEY, '1'); } catch (_) { }
    }

    function trackSeoCtaClick(event) {
        const cta = event.target?.closest?.('a.marketing-btn');
        if (!cta) return;
        const pageSlug = currentPageSlug();
        if (!landingSlugs.has(pageSlug)) return;
        let destinationSlug = '';
        try {
            destinationSlug = toSlug(new URL(cta.href, locationRef.href).pathname) || 'home';
        } catch (_) { }
        track('seo_cta_click', {
            page_slug: pageSlug,
            cta_position: cta.classList.contains('primary') ? 'primary' : 'secondary',
            destination_slug: destinationSlug
        });
    }

    function trackSeoAppOpen() {
        const pageSlug = currentPageSlug();
        if (pageSlug !== 'home') return;
        const params = new URLSearchParams(locationRef?.search || '');
        if (params.get('from') !== 'seo') return;
        const landingPage = normalizeSlug(params.get('landing') || params.get('seo_page'));
        if (!landingPage) return;
        track('app_open_from_seo', {
            landing_page: landingPage,
            cta_position: params.get('cta') || params.get('seo_cta') || 'primary'
        });
        if (global.history?.replaceState) {
            params.delete('from');
            params.delete('landing');
            params.delete('seo_page');
            params.delete('cta');
            params.delete('seo_cta');
            const query = params.toString();
            global.history.replaceState({}, documentRef?.title || '', `${locationRef.pathname}${query ? `?${query}` : ''}${locationRef.hash || ''}`);
        }
    }

    function bindProductEvents() {
        if (!global.addEventListener) return;
        global.addEventListener('memora:pdfs-imported', (event) => {
            const names = Array.isArray(event.detail?.pdfNames) ? event.detail.pdfNames : [];
            if (!names.length) return;
            track('study_flow_step', { step: 'upload' });
            track('import_started', {
                source: normalizeSource(event.detail?.source),
                source_count: names.length
            });
        });

        global.addEventListener('memora:deck-quiz-generated', (event) => {
            const questionCount = Number(event.detail?.questionCount) || 0;
            if (questionCount <= 0 || wasFirstQuizTracked()) return;
            if (track('first_quiz_generated', { question_count: questionCount })) markFirstQuizTracked();
        });

        global.addEventListener('storage', (event) => {
            if (event.key === PREFERENCE_KEY || event.key === null) {
                try { persisted = true; applyPreference(global.localStorage.getItem(PREFERENCE_KEY) !== '0'); }
                catch (_) { persisted = false; applyPreference(false); }
            }
        });
        global.addEventListener('memora:feature-opened', (event) => {
            const feature = featureByRoute[event.detail?.route];
            if (!feature || ['picker', 'transition', 'loading'].includes(event.detail?.stage)) return;
            track('feature_used', { feature, action: 'open' });
            if (feature !== 'dashboard' && feature !== 'sessions') track('study_flow_step', { step: 'study' });
        });
        const observeGeneration = (event) => {
            if (!enabled) return;
            const job = event.detail?.job;
            const feature = featureByRoute[job?.mode];
            if (!feature || !job.jobId) return;
            if (job.status === 'running' && !activeJobs.has(job.jobId)) {
                if (activeJobs.size >= 100) activeJobs.delete(activeJobs.keys().next().value);
                activeJobs.set(job.jobId, { feature, startedAt: Number(job.timings?.admittedAt || job.startedAt) || Date.now(), queuedAt: Number(job.timings?.queueEnteredAt || job.createdAt) || Date.now() });
                track('generation_started', { feature });
            }
            if (event.detail?.reason !== 'settle' || !['ready', 'partial', 'failed', 'cancelled'].includes(job.status)) return;
            const observed = activeJobs.get(job.jobId);
            if (!observed) return;
            activeJobs.delete(job.jobId);
            track('generation_result', {
                feature: observed.feature, outcome: job.status,
                duration_ms: (Number(job.settledAt) || Date.now()) - observed.startedAt,
                queue_wait_ms: observed.startedAt - observed.queuedAt,
                ...(job.usableCount == null ? {} : { usable_count: Number(job.usableCount) })
            });
            if (job.status === 'ready' || (job.status === 'partial' && Number(job.usableCount) > 0)) track('study_flow_step', { step: 'generate' });
        };

        global.addEventListener('memora:generation-job-change', observeGeneration);
        global.addEventListener('memora:analytics-generation', observeGeneration);
        global.addEventListener('memora:voice-turn-complete', () => track('feature_used', { feature: 'voice', action: 'voice_turn' }));

        global.addEventListener('memora:study-set-exported', (event) => {
            const format = String(event.detail?.format || '').trim().toLowerCase();
            const questionCount = Number(event.detail?.questionCount) || 0;
            if (!exportFormats.has(format) || questionCount <= 0) return;
            track('study_set_exported', { format, question_count: questionCount });
        });
    }

    if (documentRef?.addEventListener) documentRef.addEventListener('click', trackSeoCtaClick, true);
    if (documentRef?.addEventListener) documentRef.addEventListener('change', (event) => {
        if (event.target?.matches?.('[data-memora-analytics-toggle]')) setEnabled(event.target.checked);
    });
    if (documentRef?.addEventListener) documentRef.addEventListener('click', (event) => {
        const button = event.target?.closest?.('[data-live-rail-action]');
        if (!button || button.disabled || button.getAttribute?.('aria-disabled') === 'true') return;
        const feature = { saved: 'saved_content', 'saved-content': 'saved_content', statistics: 'stats', sources: 'sources', calendar: 'calendar', agent: 'voice' }[button.dataset?.liveRailAction];
        if (feature) track('feature_selected', { feature });
    }, true);
    bindProductEvents();
    syncPublicControls();
    trackSeoAppOpen();

    global.MemoraAnalytics = Object.freeze({
        version: 1,
        track, getState, setEnabled
    });
}(window));
